import { UserProfileCustomField } from '@features/member-management/types';
import { HIDE_APPLY_DISCOUNT_IN_CUSTOM_TERM } from '@features/settings/constants';
import { BulkPaymentDiscountType, GroupDetailsType } from '@types';
import {
  getDefaultUserProfileTemplateHeaders,
  getTemplateUserProfileDummayData
} from '@utils/member';
import { TabPill } from 'types/tabs';

import { MemberStatus } from './types';

export const CUSTOM_TERM_FREQUENCY = 'By Term: Advanced';

export enum GroupType {
  ALL = 'all',
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export const groupFilterOptions = [
  { id: 1, label: 'All groups', value: GroupType.ALL },
  { id: 2, label: 'Active groups', value: GroupType.ACTIVE },
  { id: 3, label: 'Inactive groups', value: GroupType.INACTIVE }
];

// Add New Group

export enum AddMembersTabType {
  ADD_FROM_GROUP = 'add_from_group',
  IMPORT_FILE = 'import_file',
  ADD_MANUALLY = 'add_manually',
  ADD_FROM_MEMBER_LIST = 'add_from_member_list'
}

export const addNewGroupTabs = (
  isGroupQuickCollect?: boolean,
  isOnceImmediatePayment?: boolean
) => [
  ...(!isOnceImmediatePayment
    ? [
        {
          id: AddMembersTabType.ADD_FROM_MEMBER_LIST,
          label: 'Add from members list'
        }
      ]
    : []),
  ...(isGroupQuickCollect || isOnceImmediatePayment
    ? [
        {
          id: AddMembersTabType.ADD_FROM_GROUP,
          label: 'Add from group'
        }
      ]
    : []),
  ...(!isOnceImmediatePayment
    ? [
        {
          id: AddMembersTabType.IMPORT_FILE,
          label: 'Import file'
        },
        {
          id: AddMembersTabType.ADD_MANUALLY,
          label: 'Add manually'
        }
      ]
    : [])
];

export const acceptedMemberAdditionFileFormats = '.xlsx, .xls, .csv';

export enum PaymentCollectionType {
  MONTHLY = 'monthly',
  WEEKLY = 'weekly',
  BY_TERM = 'by_term',
  ONCE = 'once',
  CUSTOM_TERM = 'custom_term',
  SESSION_BASED = 'session_based'
}

export const PaymentCollectionOptions = [
  { id: 1, label: 'Monthly', value: PaymentCollectionType.MONTHLY },
  { id: 2, label: 'Weekly', value: PaymentCollectionType.WEEKLY },
  {
    id: 3,
    label: 'By Term/Installments',
    value: PaymentCollectionType.BY_TERM
  },
  { id: 4, label: 'Once', value: PaymentCollectionType.ONCE }
];

export enum MemberType {
  ALL = 'all',
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export const memberFilterOptions = [
  { id: 1, label: 'All Members', value: MemberType.ALL },
  { id: 2, label: 'Active Members', value: MemberType.ACTIVE },
  { id: 3, label: 'Inactive Members', value: MemberType.INACTIVE }
];

export enum GroupTransactionsFilterType {
  ALL = 'all',
  GROUP = 'group',
  QUICK_COLLECT = 'individual'
}

export const groupTransactionsFilterOptions = [
  { id: 1, label: 'All Payments', value: GroupTransactionsFilterType.ALL },
  { id: 2, label: 'Group Payments', value: GroupTransactionsFilterType.GROUP },
  {
    id: 3,
    label: 'Quick Collect',
    value: GroupTransactionsFilterType.QUICK_COLLECT
  }
];

export type MemberTableRowDataType = {
  id: string;
  name: string;
  lastPaid: string;
  feeAmount: string;
  dueDate: string;
};

const AMOUNT_COLUMN_ACCESSORS = [
  'feeAmount',
  'totalAmount',
  'totalPaid',
  'totalPending',
  'totalFee',
  'paidFee',
  'pendingFee'
];

type MemberTableColumn = {
  Header: string;
  accessor: string;
  width: string;
  style?: string;
};

export const memberTableColumn = (
  isAutoDebitEnabled: boolean,
  isLeadSquared: boolean,
  isAttendanceBasedFee: boolean = false,
  isAdvancedTermGroup: boolean = false,
  isSessionBased: boolean = false,
  hasPaymentOrderViewAccess: boolean = true
) => {
  const withAmountAccessFilter = (columns: MemberTableColumn[]) =>
    hasPaymentOrderViewAccess
      ? columns
      : columns.filter(
          (column) => !AMOUNT_COLUMN_ACCESSORS.includes(column.accessor)
        );

  if (isAttendanceBasedFee) {
    return withAmountAccessFilter([
      {
        Header: 'Name',
        accessor: 'name',
        style: 'group-hover:underline !font-medium',
        width: '30%'
      },
      { Header: 'Rate per class', accessor: 'feeAmount', width: '25%' },
      ...(isSessionBased
        ? [{ Header: 'Session count', accessor: 'sessionCount', width: '25%' }]
        : [
            { Header: 'Next Requested date', accessor: 'dueDate', width: '25%' }
          ]),
      { Header: 'Actions', accessor: 'actions', width: '15%' }
    ]);
  }

  const columns = withAmountAccessFilter([
    {
      Header: 'Name',
      accessor: 'name',
      style: 'group-hover:underline !font-medium',
      width: '20%'
    },
    ...(isAdvancedTermGroup
      ? [
          {
            Header: 'Next installment amount',
            accessor: 'feeAmount',
            width: '15%'
          }
        ]
      : [{ Header: 'Amount', accessor: 'feeAmount', width: '15%' }]),

    { Header: 'Next Request Date', accessor: 'dueDate', width: '25%' },
    ...(isAdvancedTermGroup
      ? [
          { Header: 'Total amount', accessor: 'totalAmount', width: '10%' },
          { Header: 'Total paid', accessor: 'totalPaid', width: '10%' },
          { Header: 'Total pending', accessor: 'totalPending', width: '10%' }
        ]
      : []),
    ...(isLeadSquared
      ? [
          { Header: 'Course fee', accessor: 'totalFee', width: '12%' },
          { Header: 'Paid course fee', accessor: 'paidFee', width: '16%' },
          {
            Header: 'Pending course fee',
            accessor: 'pendingFee',
            width: '17%'
          }
        ]
      : []),
    ...(isAutoDebitEnabled
      ? [
          {
            Header: 'Autopay enabled',
            accessor: 'autopayEnabled',
            width: '15%'
          }
        ]
      : []),
    { Header: 'Actions', accessor: 'actions', width: '10%' }
  ]);

  const totalWidth = columns.reduce(
    (acc, column) => acc + (column.width ? parseInt(column.width, 10) : 0),
    0
  );

  return columns.map((column) => ({
    ...column,
    width: `${
      ((column.width ? parseInt(column.width, 10) : 0) / totalWidth) * 100
    }%`
  }));
};

export const attendanceTableColumn = [
  // { Header: 'Sl.No', accessor: 'slotNumber', width: '10%' },
  {
    Header: 'Name',
    accessor: 'name',
    width: '30%'
  },
  { Header: 'Status', accessor: 'status', width: '30%' },
  { Header: 'Info', accessor: 'info', width: '30%' },
  { Header: '', accessor: 'actions', width: '10%' }
];

export const addMemberTableColumn = [
  {
    Header: 'Name',
    accessor: 'name',
    style: 'group-hover:underline !font-medium',
    width: '15%'
  },
  { Header: 'Primary Number', accessor: 'primaryNumber', width: '15%' },
  { Header: 'Alternate Number', accessor: 'altNumber', width: '15%' },
  { Header: 'Amount', accessor: 'feeAmount' },
  { Header: 'Frequency', accessor: 'dueDate', width: '20%' },
  { Header: 'Start Date', accessor: 'startDate', width: '10%' },
  { Header: 'Actions', accessor: 'actions' }
];

export const getCustomTermTableColumn = (defaultTermLabel?: string) => [
  {
    Header: defaultTermLabel,
    accessor: 'term_label',
    width: '30%'
  },
  { Header: 'Amount', accessor: 'amount', width: '20%' },
  { Header: 'Due Date', accessor: 'dueDate', width: '20%' },
  { Header: '', accessor: 'actions' }
];

export const getCustomTermMemberTableColumn = (
  defaultTermLabel?: string,
  isMember?: boolean,
  memberFlow?: 'add' | 'edit',
  showAfterDiscountColumn?: boolean
) => [
  {
    Header: defaultTermLabel,
    accessor: 'term_label',
    width: '30%'
  },
  { Header: 'Amount', accessor: 'amount', width: '25%' },
  ...(isMember && memberFlow === 'edit' && showAfterDiscountColumn
    ? [
        {
          Header: 'After discount',
          accessor: 'afterDiscountAmount',
          width: '20%'
        }
      ]
    : []),
  { Header: 'Due Date', accessor: 'dueDate', width: '25%' }
];

export const customTermFeeConfigTableColumn = [
  {
    Header: 'Category',
    accessor: 'category',
    width: '40%'
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    width: '60%'
  }
];

export const userProfileHeaders = [
  'email',
  'dateOfBirth',
  'guardianName',
  'admissionNumber',
  'admissionDate',
  'addressLine1',
  'addressLine2',
  'country',
  'state',
  'city',
  'pincode'
];

export const userProfileDummyData = [
  'XXXXXX@gmail.com',
  '2024-02-14',
  'Doe',
  'A123',
  '2024-02-14',
  'Main street',
  'Near College',
  'India',
  'Karnataka',
  'Bangalore',
  '560001'
];

export const defaultTemplateHeaders = (
  feeCategories: string[],
  isEmailEnabled: boolean,
  isAmountPaidTillDateEnabled: boolean,
  isOnce: boolean,
  customFields: UserProfileCustomField[],
  termsName: string[],
  includeUserProfile = false,
  isLeadSquared = false,
  isCustomTerm = false,
  isEndDateEnabled = false,
  isAttendanceBasedFee = false,
  isSessionBased = false
) => {
  if (isAttendanceBasedFee) {
    const billingColumn = isSessionBased
      ? 'billingPeriod(numberOfClasses)'
      : 'startDate';
    return [
      'name',
      'primaryNumberCode',
      'primaryNumber',
      'altNumberCode',
      'altNumber',
      ...(isEmailEnabled && !includeUserProfile ? ['email'] : []),
      'ratePerClass',
      billingColumn,
      ...(includeUserProfile
        ? getDefaultUserProfileTemplateHeaders(userProfileHeaders, customFields)
        : [])
    ];
  }

  const haveCategory = feeCategories.length > 0;

  const termHeaderData =
    feeCategories.length > 0
      ? termsName.flatMap((term) => [
          ...feeCategories.map((fee) => `${term}_${fee.replace(/\s+/g, '')}`),
          `${term}_duedate`
        ])
      : termsName.flatMap((term) => [`${term}_Amount`, `${term}_duedate`]);

  return [
    'name',
    'primaryNumberCode',
    'primaryNumber',
    'altNumberCode',
    'altNumber',
    ...(isEmailEnabled && !includeUserProfile ? ['email'] : []),
    ...(haveCategory || isCustomTerm ? [] : ['amount']),
    ...(isAmountPaidTillDateEnabled ? ['amountPaid'] : []),
    ...(!isOnce && !isCustomTerm
      ? ['frequency', 'monthsPerTerm', 'startDate']
      : []),
    ...(isEndDateEnabled && !isOnce && !isCustomTerm ? ['endDate'] : []),
    ...(isLeadSquared ? ['courseFee'] : []),
    ...(includeUserProfile
      ? getDefaultUserProfileTemplateHeaders(userProfileHeaders, customFields)
      : []),
    ...(haveCategory && !isCustomTerm ? feeCategories : []),
    ...(isCustomTerm ? termHeaderData : []),
    ...(isCustomTerm && !HIDE_APPLY_DISCOUNT_IN_CUSTOM_TERM
      ? ['discountLabel', 'discountAmount']
      : [])
  ];
};

const getTermDummyDueDate = (termIndex: number): string => {
  const date = new Date();
  date.setMonth(date.getMonth() + termIndex + 1);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const templateDummayData = (
  haveCategory: boolean,
  isEmailEnabled: boolean,
  isAmountPaidTillDateEnabled: boolean,
  isOnce: boolean,
  customFields: UserProfileCustomField[],
  includeUserProfile = false,
  isCustomTerm = false,
  isLeadSquared = false,
  isEndDateEnabled = false,
  isAttendanceBasedFee = false,
  isSessionBased = false,
  termsCount = 0,
  feeCategoryCount = 0
) => {
  if (isAttendanceBasedFee) {
    return [
      'John',
      '91',
      'XXXXXXXXXX',
      '91',
      'XXXXXXXXXX',
      ...(isEmailEnabled && !includeUserProfile ? ['XXXXXX@gmail.com'] : []),
      '500',
      isSessionBased ? '8' : '15/01/2025',
      ...(includeUserProfile
        ? getTemplateUserProfileDummayData(userProfileDummyData, customFields)
        : [])
    ];
  }

  const termDummyData = isCustomTerm
    ? Array.from({ length: termsCount }, (_, i) =>
        feeCategoryCount > 0
          ? [...new Array(feeCategoryCount).fill('100'), getTermDummyDueDate(i)]
          : ['1000', getTermDummyDueDate(i)]
      ).flat()
    : [];

  return [
    'John',
    '91',
    'XXXXXXXXXX',
    '91',
    'XXXXXXXXXX',
    ...(isEmailEnabled && !includeUserProfile ? ['XXXXXX@gmail.com'] : []),
    ...(haveCategory || isCustomTerm ? [] : ['1000']),
    ...(isAmountPaidTillDateEnabled ? [''] : []),
    ...(!isOnce && !isCustomTerm ? ['Monthly', '', '2025-03-06'] : []),
    ...(isEndDateEnabled && !isOnce && !isCustomTerm ? ['2025-05-06'] : []),
    ...(isLeadSquared ? ['1000'] : []),
    ...(includeUserProfile
      ? getTemplateUserProfileDummayData(userProfileDummyData, customFields)
      : []),
    ...termDummyData
  ];
};

export const editGroupInfoInitialState = {
  groupPhoto: { file: '', preview: '', image_url: '' },
  name: '',
  amount: '',
  next_due_at: '',
  frequency: '',
  interval_frequency: 0,
  paymentInstallment: false,
  end_date: '',
  isAttandanceTrackingEnabled: false,
  isSendAttendanceAlertEnabled: false,
  workingDays: []
};

export const groupInfoAddMemberTableColumn = [
  {
    Header: 'Name',
    accessor: 'name',
    style: 'group-hover:underline !font-medium',
    width: '15%'
  },
  { Header: 'Primary Number', accessor: 'primaryNumber', width: '12%' },
  { Header: 'Alternate Number', accessor: 'altNumber', width: '15%' },
  { Header: 'Amount', accessor: 'feeAmount', width: '10%' },
  { Header: 'Due Date', accessor: 'dueDate', width: '15%' },
  { Header: 'Start Date', accessor: 'startDate', width: '15%' },
  { Header: 'Actions', accessor: 'actions', width: '10%' }
];

export const attendanceBasedAddMemberTableColumn = [
  {
    Header: 'Name',
    accessor: 'name',
    style: 'group-hover:underline !font-medium',
    width: '20%'
  },
  { Header: 'Primary Number', accessor: 'primaryNumber', width: '15%' },
  { Header: 'Alternate Number', accessor: 'altNumber', width: '15%' },
  { Header: 'Rate per class', accessor: 'feeAmount', width: '18%' },
  {
    Header: 'Billing Period',
    accessor: 'billingPeriod',
    width: '20%'
  },
  { Header: 'Actions', accessor: 'actions', width: '12%' }
];

export const groupStatusChangeConfirmModalInfo = (
  status: 'lite' | 'cofee'
): Record<string, any> => ({
  deactivate: {
    title: 'Are you sure you want to deactivate this group?',
    description: `Payment ${
      status === 'lite' ? 'request' : 'links'
    } will not be sent to members of deactivated group`
  },
  activate: {
    title: 'Do you want to activate this group?',
    description: `Members of this group will start receiving payment ${
      status === 'lite' ? 'request' : 'links'
    }`
  },
  activateWithEndDateClear: {
    title: 'Do you want to activate this group?',
    description:
      'The group has expired, reactivating the group will clear the end date.'
  },
  deactivateBeforeEndDate: {
    title: 'Are you sure you want to deactivate this group?',
    description: `The group has an upcoming end date, no payment ${
      status === 'lite' ? 'request' : 'links'
    } will be sent after deactivation.`
  }
});

export const memberStatusChangeConfirmModalInfo = (
  status: 'lite' | 'cofee'
): Record<string, any> => ({
  [MemberStatus.INACTIVE]: {
    title: 'Are you sure you want to deactivate',
    description: `Deactivated members will not receive payment ${
      status === 'lite' ? 'request' : 'links'
    } for the coming months`
  },
  [MemberStatus.ACTIVE]: {
    title: 'Are you sure you want to activate',
    description: `Active members will receive payment ${
      status === 'lite' ? 'request' : 'links'
    } for the coming months`
  }
});

export const groupDetailsInitialState: GroupDetailsType = {
  id: '',
  name: '',
  amount: '',
  frequency: '',
  interval_frequency: 0,
  is_recurring: false,
  is_active: false,
  member_count: 0,
  active_member_count: 0,
  next_due_at: '',
  image_url: '',
  created_at: '',
  payment_list: [],
  collection_started_at: '',
  end_date: '',
  is_attendance_tracking_enabled: false,
  is_send_attendance_alert_enabled: false,
  work_days: [],
  is_fee_configured: false
};

export const groupDetailsTabs: TabPill[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'members', label: 'Members' },
  { id: 'quick_collect', label: 'Quick Collect' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'transactions', label: 'Transactions' }
];

export const selectedMemberInitialState = {
  created_at: '',
  fee_amount: '',
  group_member_id: 0,
  is_active: false,
  is_payment_link_sent: false,
  member_id: '',
  member_image_url: '',
  name: '',
  next_due_at: '',
  payment_due: '',
  phone: '',
  secondary_mobile: '',
  isFeeCategoryEnabled: '',
  feeCategories: [],
  id: '',
  primary_country_code: '',
  secondary_country_code: '',
  email: '',
  autopay_enabled: false
};

export const nextOrCurrentMonthOptions = [
  {
    key: 'next',
    value: 'Next month'
  },
  {
    key: 'current',
    value: 'Current month'
  }
];

export const nextOrCurrentWeekOptions = [
  {
    key: 'next',
    value: 'Next week'
  },
  {
    key: 'current',
    value: 'Current week'
  }
];

export const editMemberInitialState = {
  name: '',
  primaryNumber: '',
  altNumber: '',
  amount: '',
  dueDate: '',
  id: '',
  is_payment_link_sent: false
};

export enum GroupTransactionsTabOptionsEnum {
  recent = 'all',
  paid = 'paid',
  pending = 'pending',
  partialPayment = 'partial_payment'
}

export const groupTransactionsTabOptions = [
  { key: GroupTransactionsTabOptionsEnum.recent, label: 'Recent' },
  { key: GroupTransactionsTabOptionsEnum.paid, label: 'Paid' },
  { key: GroupTransactionsTabOptionsEnum.pending, label: 'Pending' },
  {
    key: GroupTransactionsTabOptionsEnum.partialPayment,
    label: 'Partially Paid'
  }
];

export const groupAllTransactionsTabTableColumn = (
  hideMobileNumber: boolean
) => [
  { Header: 'Name', accessor: 'name', width: '17%' },
  ...(!hideMobileNumber
    ? [
        {
          Header: 'Mobile Number',
          accessor: 'phoneNumber',
          width: '13%'
        }
      ]
    : []),
  { Header: 'Admission Number', accessor: 'admissionNumber', width: '12%' },
  { Header: 'Requested on', accessor: 'dueFrom', width: '15%' },
  { Header: 'Paid On', accessor: 'paidOn', width: '13%' },
  { Header: 'Settled On', accessor: 'settledOn', width: '15%' },
  { Header: 'Amount', accessor: 'amount', width: '15%' },
  { Header: 'Actions', accessor: 'actions', width: '7%' }
];

export const groupPaidTransactionsTabTableColumn = (
  hideMobileNumber: boolean
) => [
  { Header: 'Name', accessor: 'name', width: '17%' },
  ...(!hideMobileNumber
    ? [
        {
          Header: 'Mobile Number',
          accessor: 'phoneNumber',
          width: '13%'
        }
      ]
    : []),
  { Header: 'Admission Number', accessor: 'admissionNumber', width: '12%' },
  { Header: 'Requested on', accessor: 'dueFrom', width: '15%' },
  { Header: 'Paid On', accessor: 'paidOn', width: '15%' },
  { Header: 'Settled On', accessor: 'settledOn', width: '15%' },
  { Header: 'Amount', accessor: 'amount', width: '10%' },
  { Header: 'Actions', accessor: 'actions', width: '7%' }
];

export const groupPendingTransactionsTabTableColumn = (
  hideMobileNumber: boolean
) => [
  { Header: 'Name', accessor: 'name', width: '24%' },
  ...(!hideMobileNumber
    ? [
        {
          Header: 'Mobile Number',
          accessor: 'phoneNumber',
          width: '18%'
        }
      ]
    : []),
  { Header: 'Admission Number', accessor: 'admissionNumber', width: '14%' },
  { Header: 'Requested on', accessor: 'dueFrom', width: '20%' },
  { Header: 'Amount', accessor: 'amount', width: '17%' },
  { Header: 'Actions', accessor: 'actions', width: '7%' }
];

export const addFromMembersTableColumn = (
  isQuickCollect: boolean,
  isAttendanceBasedFee: boolean = false
) => {
  if (isAttendanceBasedFee) {
    return [
      { Header: 'Name', accessor: 'name', width: '25%' },
      { Header: 'Mobile No', accessor: 'phoneNumber', width: '20%' },
      {
        Header: 'Rate per class',
        accessor: 'dueAmount',
        width: '25%'
      },
      {
        Header: 'Billing Period',
        accessor: 'billingPeriod',
        width: '20%'
      }
    ];
  }

  return [
    {
      Header: 'Name',
      accessor: 'name',
      width: isQuickCollect ? '50%' : '20%'
    },
    {
      Header: 'Mobile No',
      accessor: 'phoneNumber',
      width: isQuickCollect ? '40%' : '15%'
    },
    ...(isQuickCollect
      ? []
      : [
          { Header: 'Due Amount', accessor: 'dueAmount', width: '20%' },
          {
            Header: 'When do you want to collect fees?',
            accessor: 'dueDate',
            width: '25%'
          },
          { Header: 'Start Date', accessor: 'startDate', width: '20%' }
        ])
  ];
};

export const downloadTemplateOptions = [
  {
    label: 'Basic Template',
    value: 'Basic Template',
    subtext: 'with basic member details.'
  },
  {
    label: 'Advanced Template',
    value: 'Advanced Template',
    subtext:
      'with additional member details like address, alternate phone numbers etc.'
  }
];

export const groupMemberDateRangeOptions = [
  { id: 'all_member', label: 'All members', value: 'all_member' },
  { id: 'active_member', label: 'Active members', value: 'active_member' },
  {
    id: 'inactive_member',
    label: 'Inactive members',
    value: 'inactive_member'
  }
];

const groupMemberPaymentOptions = [
  { id: 'autopay_enabled', label: 'Yes', value: 'autopay_enabled' },
  { id: 'autopay_disabled', label: 'No', value: 'autopay_disabled' }
];

export const groupMemberFilterOptions = (isAutoDebitEnabled: boolean) => ({
  members: {
    id: 1,
    label: 'Members',
    field: 'members',
    options: groupMemberDateRangeOptions
  },
  ...(isAutoDebitEnabled && {
    autopayEnabled: {
      id: 2,
      label: 'Autopay Enabled',
      field: 'autopayEnabled',
      options: groupMemberPaymentOptions
    }
  })
});

export const groupMemberFilterDefaultValues = {
  members: [groupMemberDateRangeOptions[1].value],
  autopayEnabled: ['']
};

export const groupMemberExportColumnHeaders = [
  { key: 'name', label: 'Name' },
  { key: 'primary_number', label: 'Primary Number' },
  { key: 'alternate_number', label: 'Alternate Number' },
  { key: 'group', label: 'Group' },
  { key: 'email', label: 'Email' },
  { key: 'date_of_birth', label: 'Date of Birth' },
  { key: 'guardian_name', label: 'Guardian Name' },
  { key: 'admission_number', label: 'Admission Number' },
  { key: 'admission_date', label: 'Admission Date' },
  { key: 'address', label: 'Address' },
  { key: 'status', label: 'Status' },
  { key: 'amount', label: 'Amount' },
  { key: 'frequency', label: 'Frequency' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
  { key: 'next_request_date', label: 'Next Request Date' },
  { key: 'total_collected', label: 'Total Collected' },
  { key: 'total_pending', label: 'Total Due' }
];

export const getInitialBulkPaymentDiscountData =
  (): BulkPaymentDiscountType[] => [
    {
      threshold: { type: 'percentage', value: '' },
      discount: { type: 'percentage', value: '' }
    }
  ];

export const bulkPaymentDiscountColumn = [
  {
    Header: 'Slab',
    accessor: 'name',
    width: '22%'
  },
  {
    Header: 'Threshold (%)',
    accessor: 'threshold',
    width: '32%'
  },
  {
    Header: 'Discount (%)',
    accessor: 'discount',
    width: '32%'
  },
  {
    Header: '',
    accessor: 'actions',
    width: '14%'
  }
];
